import React, { useState } from "react";
import { Alert, Box, Chip, CircularProgress, IconButton, Paper, Popover, Skeleton, Tooltip, Typography } from "@mui/material";
import { AutoAwesome, CheckCircle, Refresh } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { useGetStatesByCountryIdQuery } from "../../redux/api/statesApi";
import { useGetCitiesByProvinceIdQuery } from "../../redux/api/citiesApi";
import { useGetSubLocationsByCityIdQuery } from "../../redux/api/sublocationApi";
import { useGetAllAmenitiesQuery } from "../../redux/api/amenitiesApi";
import {
  PROPERTY_DURATION,
  PROPERTY_FURNISHINGS,
  PROPERTY_OFF_PLANS,
  PROPERTY_PURPOSES,
  PROPERTY_TYPES,
} from "../../constants/property";
import { generateListingSuggestions } from "../../services/openaiListing";

const labelOf = (list, id) => list.find((i) => i.id === id)?.label;

/**
 * Small ✨ icon button for the Title / Description fields.
 * Opens a popover with 3 AI suggestions (+ regenerate). Picking one fills the field.
 */
export default function AiSuggestButton({ kind }) {
  const isTitle = kind === "title";
  const fieldName = isTitle ? "propertytitle" : "propertydescription";

  const { watch, setValue } = useFormContext();
  const agency = useSelector(selectCurrentagency);
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [round, setRound] = useState(0);

  const v = watch();
  const { data: states } = useGetStatesByCountryIdQuery(agency.countryid);
  const { data: cities } = useGetCitiesByProvinceIdQuery(v.provid, { skip: !v.provid });
  const { data: subLocations } = useGetSubLocationsByCityIdQuery(v.cityid, { skip: !v.cityid });
  const { data: allAmenities = [] } = useGetAllAmenitiesQuery();

  const requirements = [
    ["Purpose", v.propertypurposeid],
    ["Property type", v.propertytypeid],
    ["State or city", v.cityid || v.provid],
  ];
  const ready = requirements.every(([, done]) => !!done);

  const generate = async (next) => {
    setRound(next);
    setLoading(true);
    setError("");
    try {
      const details = {
        Purpose: labelOf(PROPERTY_PURPOSES, v.propertypurposeid),
        "Property type": labelOf(PROPERTY_TYPES, v.propertytypeid),
        Bedrooms: v.bedrooms,
        Bathrooms: v.bathrooms,
        Furnishing: v.furnished === null || v.furnished === "" ? undefined : labelOf(PROPERTY_FURNISHINGS, v.furnished),
        Status: labelOf(PROPERTY_OFF_PLANS, v.offPlan),
        "Area (kanal)": Number(v.propertysizekanal) > 0 ? v.propertysizekanal : undefined,
        "Area (sq ft)": v.propertysizesqft,
        "Area (marla)": v.propertysizemarla,
        State: states?.find((s) => s.provid === v.provid)?.provname,
        City: cities?.find((c) => c.cityid === v.cityid)?.cityname,
        "Sub location": subLocations?.find((s) => s.sublocid === v.sublocid)?.sublocation,
        Price: v.propertyprice ? `${v.propertyprice}${agency.currency ? " " + agency.currency : ""}` : undefined,
        "Payment duration": labelOf(PROPERTY_DURATION, v.duration),
        Amenities: (v.amenitiesList || [])
          .map((id) => allAmenities.find((a) => a.amenitiesid === id)?.amenities)
          .filter(Boolean),
      };
      setItems(await generateListingSuggestions(details, kind, next));
    } catch (e) {
      setError(e.message || "Could not generate suggestions.");
    } finally {
      setLoading(false);
    }
  };

  const open = (e) => {
    setAnchorEl(e.currentTarget);
    if (ready && !items) generate(1);
  };

  const pick = (text) => {
    setValue(fieldName, text, { shouldValidate: true, shouldDirty: true });
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={isTitle ? "Suggest titles with Realtors AI" : "Suggest descriptions with Realtors AI"}>
        <IconButton
          size="small"
          color="primary"
          onClick={open}
          sx={{ ml: 0.5, ...(isTitle ? {} : { alignSelf: "flex-start", mt: -0.25 }) }}
          aria-label="AI suggestions"
        >
          <AutoAwesome fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: { xs: "calc(100vw - 32px)", sm: isTitle ? 420 : 560 }, maxHeight: "70vh", borderRadius: 2, mt: 0.5 } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
          <AutoAwesome color="primary" fontSize="small" />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {isTitle ? "Title suggestions" : "Description suggestions"}
          </Typography>
          {ready && (
            <Tooltip title="Suggest 3 new options">
              <span>
                <IconButton size="small" onClick={() => generate(round + 1)} disabled={loading} aria-label="Regenerate">
                  {loading ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ p: 2, display: "grid", gap: 1.25, overflowY: "auto" }}>
          {!ready ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Fill in these details first so the suggestions are accurate:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {requirements.map(([label, done]) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    variant={done ? "filled" : "outlined"}
                    color={done ? "primary" : "default"}
                    icon={done ? <CheckCircle /> : undefined}
                    sx={done ? { color: "white", "& .MuiChip-icon": { color: "white" } } : undefined}
                  />
                ))}
              </Box>
            </>
          ) : (
            <>
              {error && <Alert severity="error">{error}</Alert>}
              {loading &&
                [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={isTitle ? 44 : 110} />)}
              {!loading &&
                items?.map((text, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    onClick={() => pick(text)}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      gap: 1.5,
                      alignItems: "flex-start",
                      transition: "all .15s",
                      "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                    }}
                  >
                    <Chip size="small" label={i + 1} sx={{ mt: 0.25 }} />
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{text}</Typography>
                  </Paper>
                ))}
              {!loading && items && (
                <Typography variant="caption" color="text.secondary">
                  Click an option to use it. Not happy? Press ↻ for 3 new ones.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}
